import { useState } from "react";
import { Mail, Send, CheckCircle2 } from "lucide-react";
import { CONTACT_REASONS, EMAILS } from "@/lib/emails";
import { submitContact } from "@/lib/api";

export default function ContactPage() {
  const [reason, setReason] = useState(CONTACT_REASONS[0].key);
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState("");

  const selected = CONTACT_REASONS.find((r) => r.key === reason);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name || !form.email || !form.message) {
      setError("Merci de renseigner votre nom, votre email et un message.");
      return;
    }
    setSending(true);
    try {
      const res = await submitContact({ ...form, reason });
      setSuccess(res);
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (e) {
      setError("Une erreur est survenue. Reessayez dans quelques instants.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main className="max-w-6xl mx-auto px-6 py-10" data-testid="contact-page">
      <div className="mb-8">
        <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight">Nous contacter</h1>
        <p className="text-[var(--ink-soft)] mt-2">
          Choisissez le service que vous souhaitez joindre. Votre message sera transmis a l'equipe Fodindin par email.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-8">
        {/* Sidebar: 5 contact emails */}
        <aside className="space-y-3" data-testid="contact-emails-list">
          {CONTACT_REASONS.map((r) => (
            <button
              key={r.key}
              onClick={() => setReason(r.key)}
              className={`w-full text-left glass-card p-4 transition-transform hover:translate-y-[-2px] ${
                reason === r.key ? "ring-2 ring-orange-500" : ""
              }`}
              data-testid={`contact-reason-${r.key}`}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-orange-50 text-orange-600 grid place-items-center"><Mail size={16} /></div>
                <div className="flex-1">
                  <div className="font-bold text-sm">{r.label}</div>
                  <a
                    href={`mailto:${r.email}`}
                    onClick={(e) => e.stopPropagation()}
                    className="text-xs text-orange-600 font-semibold"
                    data-testid={`contact-mailto-${r.key}`}
                  >
                    {r.email}
                  </a>
                </div>
              </div>
              <div className="text-xs text-[var(--ink-soft)] mt-2">{r.desc}</div>
            </button>
          ))}
        </aside>

        {/* Form */}
        <section className="glass-card p-7" data-testid="contact-form-card">
          {success ? (
            <div className="text-center py-10" data-testid="contact-success">
              <div className="w-14 h-14 rounded-full bg-green-100 text-green-600 grid place-items-center mx-auto mb-4">
                <CheckCircle2 size={28} />
              </div>
              <div className="font-bold text-xl mb-1">Message bien envoye !</div>
              <p className="text-sm text-[var(--ink-soft)]">
                Votre demande a ete transmise a <b>{success.to}</b>.
                <br />Nous vous repondrons dans les meilleurs delais.
              </p>
              <button onClick={() => setSuccess(null)} className="btn-secondary mt-6" data-testid="contact-new-btn">
                Envoyer un autre message
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
              <div className="text-xs text-[var(--ink-soft)]">
                Destination : <b className="text-orange-600">{selected?.email}</b>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label>Votre nom</label>
                  <input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Aya Kouame"
                    data-testid="contact-name"
                  />
                </div>
                <div>
                  <label>Votre email</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="vous@email.com"
                    data-testid="contact-email"
                  />
                </div>
              </div>
              <div>
                <label>Sujet</label>
                <input
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  placeholder="Resume en quelques mots..."
                  data-testid="contact-subject"
                />
              </div>
              <div>
                <label>Message</label>
                <textarea
                  rows={6}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="Decrivez votre demande..."
                  data-testid="contact-message"
                />
              </div>
              {error && (
                <div className="text-sm text-red-600 font-semibold" data-testid="contact-error">{error}</div>
              )}
              <button
                type="submit"
                disabled={sending}
                className="btn-primary inline-flex items-center gap-2"
                data-testid="contact-submit"
              >
                <Send size={14} /> {sending ? "Envoi..." : "Envoyer le message"}
              </button>

              <div className="text-xs text-[var(--ink-soft)] pt-2">
                Ou ecrivez-nous directement a{" "}
                <a href={`mailto:${EMAILS.contact}`} className="text-orange-600 font-semibold">{EMAILS.contact}</a>.
                Pour un probleme technique, utilisez{" "}
                <a href={`mailto:${EMAILS.support}`} className="text-orange-600 font-semibold">{EMAILS.support}</a>.
              </div>
            </form>
          )}
        </section>
      </div>
    </main>
  );
}
